import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { FormField } from '../components/ui/FormField';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { MasterDataImportExport } from '../components/admin/MasterDataImportExport';
import { apiService } from '../services/apiService';

interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  price: number;
  intCoins: number;
  durationDays: number;
  intCoinsExpirationDays: number;
  features: string[];
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

interface SubscriptionPlanFormData {
  name: string;
  description: string;
  price: number;
  intCoins: number;
  durationDays: number;
  intCoinsExpirationDays: number;
  features: string[];
  status: 'active' | 'inactive';
}

export const AdminSubscriptionPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState<SubscriptionPlanFormData>({
    name: '',
    description: '',
    price: 0,
    intCoins: 0,
    durationDays: 30,
    intCoinsExpirationDays: 365,
    features: [],
    status: 'active'
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAdminSubscriptionPlans();
      setPlans(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (formData.price <= 0) errors.price = 'Price must be greater than 0';
    if (formData.intCoins <= 0) errors.intCoins = 'Int Coins must be greater than 0';
    if (formData.durationDays <= 0) errors.durationDays = 'Duration days must be greater than 0';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      console.log('Creating subscription plan with data:', formData);
      const result = await apiService.createAdminSubscriptionPlan(formData);
      console.log('Subscription plan created successfully:', result);
      setShowCreateForm(false);
      setFormData({
        name: '',
        description: '',
        price: 0,
        intCoins: 0,
        durationDays: 30,
        intCoinsExpirationDays: 365,
        features: [],
        status: 'active'
      });
      setFormErrors({});
      await fetchPlans();
    } catch (err: any) {
      setError(err.message || 'Failed to create subscription plan');
    }
  };

  const handleUpdatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingPlan) return;
    
    // Validate form
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (formData.price <= 0) errors.price = 'Price must be greater than 0';
    if (formData.intCoins <= 0) errors.intCoins = 'Int Coins must be greater than 0';
    if (formData.durationDays <= 0) errors.durationDays = 'Duration days must be greater than 0';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      console.log('Updating subscription plan with data:', formData);
      const result = await apiService.updateAdminSubscriptionPlan(editingPlan.id, formData);
      console.log('Subscription plan updated successfully:', result);
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        price: 0,
        intCoins: 0,
        durationDays: 30,
        intCoinsExpirationDays: 365,
        features: [],
        status: 'active'
      });
      setFormErrors({});
      await fetchPlans();
    } catch (err: any) {
      setError(err.message || 'Failed to update subscription plan');
    }
  };

  const handleDeletePlan = async (planId: number) => {
    if (!window.confirm('Are you sure you want to delete this subscription plan?')) {
      return;
    }

    try {
      await apiService.deleteAdminSubscriptionPlan(planId);
      await fetchPlans();
    } catch (err: any) {
      setError(err.message || 'Failed to delete subscription plan');
    }
  };

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      price: plan.price,
      intCoins: plan.intCoins,
      durationDays: plan.durationDays,
      intCoinsExpirationDays: plan.intCoinsExpirationDays,
      features: plan.features,
      status: plan.status
    });
    setFormErrors({});
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      intCoins: 0,
      durationDays: 30,
      intCoinsExpirationDays: 365,
      features: [],
      status: 'active'
    });
    setFormErrors({});
  };

  const handleInputChange = (field: keyof SubscriptionPlanFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFeatureChange = (index: number, value: string) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const addFeature = () => {
    setFormData(prev => ({ ...prev, features: [...prev.features, ''] }));
  };

  const removeFeature = (index: number) => {
    setFormData(prev => ({ 
      ...prev, 
      features: prev.features.filter((_, i) => i !== index) 
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner text="Loading subscription plans..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Subscription Plans Management"
        subtitle="Create, edit, and manage subscription plans"
        align="center"
      >
        <div className="flex items-center gap-3">
          <MasterDataImportExport resource="subscription_plans" onComplete={fetchPlans} />
          <Button
            variant="primary"
            onClick={() => setShowCreateForm(true)}
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            }
          >
            Create Plan
          </Button>
        </div>
      </PageHeader>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {(showCreateForm || editingPlan) && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingPlan ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
            </h3>
            
            <form onSubmit={editingPlan ? handleUpdatePlan : handleCreatePlan} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  label="Plan Name"
                  htmlFor="name"
                  required
                  error={formErrors.name}
                >
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="Enter plan name..."
                  />
                </FormField>

                <FormField
                  label="Price (₹)"
                  htmlFor="price"
                  required
                  error={formErrors.price}
                >
                  <input
                    type="number"
                    id="price"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </FormField>

                <FormField
                  label="Int Coins"
                  htmlFor="intCoins"
                  required
                  error={formErrors.intCoins}
                >
                  <input
                    type="number"
                    id="intCoins"
                    value={formData.intCoins}
                    onChange={(e) => handleInputChange('intCoins', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="0"
                    min="0"
                  />
                </FormField>

                <FormField
                  label="Duration (Days)"
                  htmlFor="durationDays"
                  required
                  error={formErrors.durationDays}
                >
                  <input
                    type="number"
                    id="durationDays"
                    value={formData.durationDays}
                    onChange={(e) => handleInputChange('durationDays', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="30"
                    min="1"
                  />
                </FormField>

                <FormField
                  label="Int Coins Expiration (Days)"
                  htmlFor="intCoinsExpirationDays"
                  required
                  error={formErrors.intCoinsExpirationDays}
                >
                  <input
                    type="number"
                    id="intCoinsExpirationDays"
                    value={formData.intCoinsExpirationDays}
                    onChange={(e) => handleInputChange('intCoinsExpirationDays', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="365"
                    min="0"
                  />
                </FormField>

                <FormField
                  label="Status"
                  htmlFor="status"
                >
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </FormField>
              </div>

              <FormField
                label="Description"
                htmlFor="description"
                required
                error={formErrors.description}
              >
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  rows={3}
                  placeholder="Enter plan description..."
                />
              </FormField>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Features
                </label>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="Enter feature..."
                      />
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => removeFeature(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addFeature}
                  >
                    Add Feature
                  </Button>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={editingPlan ? handleCancelEdit : () => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </Button>
              </div>
            </form>
          </div>
        </Card>
      )}

      {/* Plans List */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Subscription Plans</h3>
          
          {plans.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No subscription plans</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new subscription plan.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map(plan => (
                <div key={plan.id} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/80 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{plan.name}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{plan.description}</p>
                    </div>
                    <Badge variant={plan.status === 'active' ? 'success' : 'warning'}>
                      {plan.status}
                    </Badge>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Price:</span>
                      <span className="text-sm font-medium">₹{plan.price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Int Coins:</span>
                      <span className="text-sm font-medium">{plan.intCoins}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Duration:</span>
                      <span className="text-sm font-medium">{plan.durationDays} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Coins Expiry:</span>
                      <span className="text-sm font-medium">{plan.intCoinsExpirationDays} days</span>
                    </div>
                  </div>

                  {plan.features.length > 0 && (
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Features:</h5>
                      <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center">
                            <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEditPlan(plan)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeletePlan(plan.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}; 